/* eslint-disable react/no-unknown-property */
import React from 'react'

import { DueOutFetchArgs } from '../../../assets/ts/data/dueout/dueout'
import { STATUS_CODE as DUEOUT_STATUS_CODES } from '../../../assets/ts/data/dueout/dueout-status-metadata'
import { InquiryFetchArgs } from '../../../assets/ts/data/inquiry/inquiry'
import { STATUS_CODE } from '../../../assets/ts/data/inquiry/inquiry-status-metadata'
import { defaultToEmptyString, twoDigits } from '../../../assets/ts/formatting/formatting'
import useDueOutStatusCounts from '../../../hooks/dueout/use-dueout-status-counts'
import useInquiryStatusCounts from '../../../hooks/inquiry/use-inquiry-status-counts'
import { PageType } from '../kpis'

type StatusCount = {
  DueOutGUID_count?: number
  InquiryGUID_count?: number
  StatusCode: string
}

const countByStatus = (
  statusCodes: string[] = [],
  invert = false,
  statusCounts: StatusCount[] = []
): string => {
  const filterFunc = invert
    ? (item: StatusCount) => !statusCodes.includes(item.StatusCode)
    : (item: StatusCount) => statusCodes.includes(item.StatusCode)

  const count = statusCounts.filter(filterFunc).reduce((acc, item) => {
    const countValue = item.InquiryGUID_count ?? item.DueOutGUID_count ?? 0
    return acc + countValue
  }, 0)

  return count.toLocaleString()
}

interface Props {
  filter?: DueOutFetchArgs | InquiryFetchArgs
  initialKpiFilters?: DueOutFetchArgs | InquiryFetchArgs
  pageType: PageType
  setKpiName: any
  tab?: 'DUEOUTS' | 'INQUIRIES'
  totalCount: any
  updateKpiFilter: (e, tab?) => void
}

const SummaryKPIs: React.FC<Props> = ({
  filter,
  initialKpiFilters,
  pageType,
  setKpiName,
  tab,
  totalCount,
  updateKpiFilter,
}) => {
  const { inquiryStatusCounts } = useInquiryStatusCounts({
    ...filter,
    ...initialKpiFilters,
    customerActionRequiredFilter: false,
    statusCodesFilter: undefined,
  })

  const { dueOutStatusCounts } = useDueOutStatusCounts({
    ...filter,
    ...initialKpiFilters,
    statusCodesFilter: undefined,
  })
  const allCount = (statusCounts: StatusCount[] = []) => {
    const count = statusCounts.reduce((acc, item) => {
      const countValue = item.InquiryGUID_count ?? item.DueOutGUID_count ?? 0
      return acc + countValue
    }, 0)
    return count.toLocaleString()
  }
  const completedInquiriesKPIs = [
    {
      colorcode: '2',
      count: defaultToEmptyString(totalCount),
      link: '/',
      statuscode: STATUS_CODE.ALL,
      title: 'All',
    },
    {
      colorcode: '25',
      count: defaultToEmptyString(
        twoDigits(countByStatus([STATUS_CODE.RESOLVED], false, inquiryStatusCounts))
      ),
      link: '/',
      statuscode: STATUS_CODE.RESOLVED,
      title: 'Resolved',
    },
    {
      colorcode: '26',
      count: defaultToEmptyString(
        twoDigits(countByStatus([STATUS_CODE.CLOSED], false, inquiryStatusCounts))
      ),
      link: '/',
      statuscode: STATUS_CODE.CLOSED,
      title: 'Closed',
    },
    {
      colorcode: '27',
      count: defaultToEmptyString(
        twoDigits(countByStatus([STATUS_CODE.CANCELED], false, inquiryStatusCounts))
      ),
      link: '/',
      statuscode: STATUS_CODE.CANCELED,
      title: 'Canceled',
    },
  ]
  const completedDueoutsKPIs = [
    {
      colorcode: '2',
      count: defaultToEmptyString(totalCount),
      link: '/',
      statuscode: STATUS_CODE.ALL,
      title: 'All',
    },
    {
      colorcode: '26',
      count: defaultToEmptyString(
        twoDigits(countByStatus([DUEOUT_STATUS_CODES.CLOSED], false, dueOutStatusCounts))
      ),
      link: '/',
      statuscode: DUEOUT_STATUS_CODES.CLOSED,
      title: 'Closed',
    },
    {
      colorcode: '27',
      count: defaultToEmptyString(
        twoDigits(countByStatus([DUEOUT_STATUS_CODES.CANCELED], false, dueOutStatusCounts))
      ),
      link: '/',
      statuscode: DUEOUT_STATUS_CODES.CANCELED,
      title: 'Canceled',
    },
  ]
  const createKpi = (colorcode, count, link, title) => ({
    colorcode,
    count: twoDigits(count),
    link,
    title,
  })

  const summarykpis = {
    myDueouts: [
      createKpi('2', allCount(dueOutStatusCounts), { ...initialKpiFilters }, 'All'),
      createKpi(
        '28',
        defaultToEmptyString(
          countByStatus([DUEOUT_STATUS_CODES.NOT_STARTED], false, dueOutStatusCounts)
        ),
        { statusCodesFilter: [DUEOUT_STATUS_CODES.NOT_STARTED] },
        'Not Started'
      ),
      createKpi(
        '24',
        defaultToEmptyString(
          countByStatus([DUEOUT_STATUS_CODES.IN_PROGRESS], false, dueOutStatusCounts)
        ),
        { statusCodesFilter: [DUEOUT_STATUS_CODES.IN_PROGRESS] },
        'Inprogress'
      ),
      createKpi(
        '29',
        defaultToEmptyString(
          countByStatus([DUEOUT_STATUS_CODES.COMPLETED], false, dueOutStatusCounts)
        ),
        { statusCodesFilter: [DUEOUT_STATUS_CODES.COMPLETED] },

        'Completed'
      ),
      createKpi(
        '26',
        defaultToEmptyString(
          countByStatus([DUEOUT_STATUS_CODES.CLOSED], false, dueOutStatusCounts)
        ),
        { statusCodesFilter: [DUEOUT_STATUS_CODES.CLOSED] },

        'Closed'
      ),
      createKpi(
        '27',
        defaultToEmptyString(
          countByStatus([DUEOUT_STATUS_CODES.CANCELED], false, dueOutStatusCounts)
        ),
        { statusCodesFilter: [DUEOUT_STATUS_CODES.CANCELED] },

        'Canceled'
      ),
    ],
    myInquiries: [
      createKpi(
        '2',
        defaultToEmptyString(allCount(inquiryStatusCounts)),
        { ...initialKpiFilters },
        'All'
      ),
      createKpi(
        '4',
        defaultToEmptyString(countByStatus([STATUS_CODE.SUBMITTED], false, inquiryStatusCounts)),
        { statusCodesFilter: [STATUS_CODE.SUBMITTED] },
        'Submitted'
      ),
      createKpi(
        '23',
        defaultToEmptyString(countByStatus([STATUS_CODE.REOPENED], false, inquiryStatusCounts)),
        { statusCodesFilter: [STATUS_CODE.REOPENED] },
        'Reopened'
      ),
      createKpi(
        '24',
        defaultToEmptyString(
          countByStatus(
            [
              STATUS_CODE.REOPENED,
              STATUS_CODE.CANCELED,
              STATUS_CODE.CLOSED,
              STATUS_CODE.RESOLVED,
              STATUS_CODE.SAVED,
              STATUS_CODE.SUBMITTED,
            ],
            true,
            inquiryStatusCounts
          )
        ),
        { activeFilter: true },
        'Inprogress'
      ),
      createKpi(
        '25',
        defaultToEmptyString(countByStatus([STATUS_CODE.RESOLVED], false, inquiryStatusCounts)),
        { statusCodesFilter: [STATUS_CODE.RESOLVED] },
        'Resolved'
      ),
      createKpi(
        '26',
        defaultToEmptyString(countByStatus([STATUS_CODE.CLOSED], false, inquiryStatusCounts)),
        { statusCodesFilter: [STATUS_CODE.CLOSED] },
        'Closed'
      ),
      createKpi(
        '27',
        defaultToEmptyString(countByStatus([STATUS_CODE.CANCELED], false, inquiryStatusCounts)),
        { statusCodesFilter: [STATUS_CODE.CANCELED] },
        'Canceled'
      ),
    ],
  }
  const dueoutKPis = [
    createKpi(
      '2',
      defaultToEmptyString(allCount(dueOutStatusCounts)),
      { ...initialKpiFilters },
      'All'
    ),
    createKpi(
      '28',
      defaultToEmptyString(
        countByStatus([DUEOUT_STATUS_CODES.NOT_STARTED], false, dueOutStatusCounts)
      ),
      { statusCodesFilter: [DUEOUT_STATUS_CODES.NOT_STARTED] },
      'Not Started'
    ),
    createKpi(
      '24',
      defaultToEmptyString(
        countByStatus([DUEOUT_STATUS_CODES.IN_PROGRESS], false, dueOutStatusCounts)
      ),
      { statusCodesFilter: [DUEOUT_STATUS_CODES.IN_PROGRESS] },
      'Inprogress'
    ),
    createKpi(
      '29',
      defaultToEmptyString(
        countByStatus([DUEOUT_STATUS_CODES.COMPLETED], false, dueOutStatusCounts)
      ),
      { statusCodesFilter: [DUEOUT_STATUS_CODES.COMPLETED] },

      'Completed'
    ),
  ]

  let kpis

  if (pageType === 'COMPLETED' && tab === 'INQUIRIES') {
    kpis = completedInquiriesKPIs.map((kpiItem) => {
      return (
        <div
          className='col col-md-6 align-items-stretch d-flex'
          key={kpiItem.title}
          onClick={(e) => {
            if (kpiItem.title === 'All') {
              updateKpiFilter({ ...filter }, tab)
            } else {
              updateKpiFilter(
                {
                  statusCodesFilter: [kpiItem.statuscode],
                  statusCodesOmittedFilter: [],
                },
                tab
              )
            }
          }}
        >
          <div className='bordered2 border-radius w-100 p-1 cursor'>
            <span
              aria-live='polite'
              className='kpiborder border-radius kpi-before align-items-center p-relative text-decoration-none d-flex flex-column w-100'
              status-kpis={kpiItem.colorcode}
              tabIndex={0}
              title={defaultToEmptyString(kpiItem.title)}
            >
              <span className='font-14'>{kpiItem.count}</span>
              <span className='font-10 px-2 latobold text-nowrap text-uppercase'>
                {defaultToEmptyString(kpiItem.title)}
              </span>
            </span>
          </div>
        </div>
      )
    })
  } else if (pageType === 'COMPLETED' && tab === 'DUEOUTS') {
    kpis = completedDueoutsKPIs.map((kpiItem) => {
      return (
        <div
          className='col col-md-6 align-items-stretch d-flex'
          key={kpiItem.title}
          onClick={() => {
            if (kpiItem.title === 'All') {
              updateKpiFilter({ ...filter }, tab)
            } else {
              updateKpiFilter(
                {
                  statusCodesFilter: [kpiItem.statuscode],
                  statusCodesOmittedFilter: [],
                },
                tab
              )
            }
          }}
        >
          <div className='bordered2 border-radius w-100 p-1 cursor'>
            <span
              aria-live='polite'
              className='kpiborder border-radius kpi-before align-items-center p-relative text-decoration-none d-flex flex-column w-100'
              status-kpis={kpiItem.colorcode}
              tabIndex={0}
              title={defaultToEmptyString(kpiItem.title)}
            >
              <span className='font-14'>{defaultToEmptyString(kpiItem.count)}</span>
              <span className='font-10 px-2 latobold text-nowrap text-uppercase'>
                {defaultToEmptyString(kpiItem.title)}
              </span>
            </span>
          </div>
        </div>
      )
    })
  } else if (pageType === 'MY DUEOUTS' || pageType === 'MY INQUIRIES') {
    const kpiArray = pageType === 'MY INQUIRIES' ? summarykpis.myInquiries : summarykpis.myDueouts
    kpis = kpiArray.map((kpiItem) => {
      return (
        <div
          className='col col-md-6 align-items-stretch d-flex'
          key={kpiItem.title}
          onClick={() => {
            updateKpiFilter(kpiItem.link)
          }}
        >
          <div className='bordered2 border-radius w-100 p-1 cursor'>
            <span
              aria-live='polite'
              className='kpiborder border-radius kpi-before align-items-center p-relative text-decoration-none d-flex flex-column w-100'
              status-kpis={kpiItem.colorcode}
              tabIndex={0}
              title={defaultToEmptyString(kpiItem.title)}
            >
              <span className='font-14'>{kpiItem.count}</span>
              <span className='font-10 px-2 latobold text-nowrap text-uppercase'>
                {defaultToEmptyString(kpiItem.title)}
              </span>
            </span>
          </div>
        </div>
      )
    })
  } else {
    kpis = dueoutKPis.map((kpiItem) => {
      return (
        <div
          className='col col-md-6 align-items-stretch d-flex'
          key={kpiItem.title}
          onClick={() => updateKpiFilter(kpiItem.link)}
        >
          <div className='bordered2 border-radius w-100 p-1 cursor'>
            <span
              aria-live='polite'
              className='kpiborder border-radius kpi-before align-items-center p-relative text-decoration-none d-flex flex-column w-100'
              status-kpis={kpiItem.colorcode}
              tabIndex={0}
              title={defaultToEmptyString(kpiItem.title)}
            >
              <span className='font-14'>{kpiItem.count}</span>
              <span className='font-10 px-2 latobold text-nowrap text-uppercase'>
                {defaultToEmptyString(kpiItem.title)}
              </span>
            </span>
          </div>
        </div>
      )
    })
  }
  return kpis
}

export default SummaryKPIs
